import React from "react";
import StatCard from "../../StatCard";
import { CashIcon, RevenueIcon } from "../../ui/Icons";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// CLOSER STATS
// =============================================================================
// PURPOSE: Dashboard section for closer role — cash/revenue-focused KPIs.
//
// VISIBILITY: Only shown to closers (see roleConfig.ts).
//
// DISPLAYED METRICS:
// - Cash collected, gross revenue, AOV (new deals only)
// - Installment tracking (collected amount + payment count)
// - Performance rates: show rate, close rate, slippage rate (alert if >20%)
// - Rev/cash ratio (tracks collection efficiency)
//
// DESIGN DECISION: Closers care about cash in the door and conversion rates.
// Other roles (setters, dialers) have different priorities and see different views.
// =============================================================================

interface CloserStatsProps {
  kpis: {
    cashCollected: number;
    revenueClosed: number;
    aov: number;
    installmentsCollected: number;
    installmentsCount: number;
    showUpRate: number;
    closeRate: number;
    slippageRate: number;
    revenueToCashRatio: string;
    cashChange?: number;
    revenueChange?: number;
    aovChange?: number;
    closeRateChange?: number;
    showUpRateChange?: number;
  };
  getSamples: (key: string) => any[];
}

const CloserStats: React.FC<CloserStatsProps> = ({ kpis, getSamples }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
    <StatCard
      title="Cash Collected"
      value={formatUSD(kpis.cashCollected)}
      icon={<CashIcon />}
      percentChange={kpis.cashChange}
      metricKey="cashCollected"
      getSamples={getSamples}
    />
    <StatCard
      title="Gross Revenue"
      value={formatUSD(kpis.revenueClosed)}
      icon={<RevenueIcon />}
      percentChange={kpis.revenueChange}
      metricKey="revenueClosed"
      getSamples={getSamples}
    />
    <StatCard
      title="AOV"
      value={formatUSD(kpis.aov)}
      subtext="New Deals Only"
      percentChange={kpis.aovChange}
      changeFormat="dollar"
      metricKey="aov"
      getSamples={getSamples}
    />
    <StatCard
      title="Installments"
      value={formatUSD(kpis.installmentsCollected)}
      subtext={`${kpis.installmentsCount} payments`}
      metricKey="installmentsCollected"
      getSamples={getSamples}
    />
    <StatCard
      title="Show Rate"
      value={`${kpis.showUpRate.toFixed(1)}%`}
      percentChange={kpis.showUpRateChange}
      metricKey="showUpRate"
      getSamples={getSamples}
    />
    <StatCard
      title="Close Rate"
      value={`${kpis.closeRate.toFixed(1)}%`}
      percentChange={kpis.closeRateChange}
      metricKey="closeRate"
      getSamples={getSamples}
    />
    {/* Slippage Rate parked — undefined to users, will revisit with proper label */}
    <StatCard
      title="Rev / Cash"
      value={kpis.revenueToCashRatio}
      subtext={kpis.revenueToCashRatio === "1.00:1" ? "Fully Collected" : "Revenue to Cash"}
      metricKey="revenueToCashRatio"
      getSamples={getSamples}
    />
  </div>
);

export default CloserStats;
